import React from 'react';
import './ResultModal.css';

interface DetectorScore {
  name: string;
  score: number;
  passed: boolean;
}

interface ResultModalProps {
  isOpen: boolean;
  onClose: () => void;
  aiScore: number | null;
  isAnalyzing: boolean;
  detectorScores?: DetectorScore[];
}

const ResultModal: React.FC<ResultModalProps> = ({ isOpen, onClose, aiScore, isAnalyzing, detectorScores }) => {
  if (!isOpen) return null;

  const getScoreColor = (score: number) => {
    if (score <= 30) return '#4CAF50'; // Green - Low AI
    if (score <= 70) return '#FF9800'; // Orange - Medium AI
    return '#F44336'; // Red - High AI
  };

  const getProgressWidth = (score: number) => {
    return `${score}%`;
  };

  const getDetectorStatus = (score: number) => {
    if (score < 40) return 'success'; // Passed - Low AI detection
    if (score <= 60) return 'warning'; // Medium - Some AI detection
    return 'error'; // Failed - High AI detection
  };

  const getDetectorIcon = (status: string) => {
    switch (status) {
      case 'success': return '✓';
      case 'warning': return '⚠';
      case 'error': return '✗';
      default: return '?';
    }
  };

  const getDetectorColor = (status: string) => {
    switch (status) {
      case 'success': return '#4CAF50';
      case 'warning': return '#FF9800';
      case 'error': return '#F44336';
      default: return '#666';
    }
  };

  return (
    <div className="result-modal-overlay" onClick={onClose}>
      <div className="result-modal" onClick={e => e.stopPropagation()}>
        <div className="result-modal-header">
          <h3>Resultado</h3>
          <button className="result-modal-close" onClick={onClose}>×</button>
        </div>
        
        <div className="result-modal-content">
          {isAnalyzing ? (
            <div className="result-loading">
              <div className="loading-spinner"></div>
              <p>Analisando texto...</p>
            </div>
          ) : aiScore !== null ? (
            <>
              <div className="score-display">
                <div className="score-percentage">{100 - aiScore}%</div>
                <div className="score-label">HUMAN WRITTEN</div>
              </div>

              <div className="progress-bar">
                <div
                  className="progress-fill"
                  style={{
                    width: getProgressWidth(100 - aiScore),
                    backgroundColor: getScoreColor(aiScore)
                  }}
                ></div>
              </div>
              
              <div className="detectors-grid">
                {detectorScores && detectorScores.length > 0 ? (
                  detectorScores.map((detector, index) => {
                    const status = getDetectorStatus(detector.score);
                    return (
                      <div key={index} className="detector-item">
                        <span
                          className="detector-icon"
                          style={{ color: getDetectorColor(status) }}
                        >
                          {getDetectorIcon(status)}
                        </span>
                        <span className="detector-name">{detector.name}</span>
                        <span className="detector-score" style={{ color: getDetectorColor(status) }}>
                          {detector.score}%
                        </span>
                      </div>
                    );
                  })
                ) : (
                  // Fallback to show analyzing state
                  ['GPTZero', 'Turnitin', 'ZeroGPT', 'Copyleaks', 'QuillBot', 'Grammarly'].map((name, index) => (
                    <div key={index} className="detector-item">
                      <span className="detector-icon" style={{ color: '#666' }}>?</span>
                      <span className="detector-name">{name}</span>
                      <span className="detector-score">--</span>
                    </div>
                  ))
                )}
              </div>
            </>
          ) : (
            <div className="no-result">
              <p>Nenhum resultado disponível</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResultModal;